import { useMemo } from "react";

import { Chain } from "chainConfigs";
import { useState } from "react";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { WalletAddresses } from "store/user-store-v2";
import { Row } from "v2/components/base/Flex/ui";
import SolanaIcon from "assets/icons/chains/solana.png";
import EthereumIcon from "assets/icons/chains/ethereum.png";
import TonIcon from "assets/icons/chains/ton.png";
import BaseIcon from "assets/icons/chains/base.png";
import BeraIcon from "assets/icons/chains/bera.png";
import BinanceIcon from "assets/icons/chains/bsc.png";
import { Typography } from "@mui/material";
import { copyToClipboard } from "utils";

export interface CrossChainConfig {
  id: string;
  symbol: string;
  networkName: React.ReactNode;
  walletName: keyof WalletAddresses;
}

export const crossChainConfigs: Record<string, CrossChainConfig> = {
  ton: {
    id: "ton",
    symbol: "TON",
    networkName: (
      <Row type="sc" sx={{ gap: "12px" }}>
        <img src={TonIcon} alt="TON" width="25px" height="24px" />
        <Typography variant="body-md">The Open Network (TON)</Typography>
      </Row>
    ),
    walletName: "tonAddress",
  },
  solana: {
    id: "solana",
    symbol: "SOL",
    networkName: (
      <Row type="sc" sx={{ gap: "12px" }}>
        <img src={SolanaIcon} alt="SOL" width="25px" height="24px" />
        <Typography variant="body-md">Solana</Typography>
      </Row>
    ),
    walletName: "solAddress",
  },
  ethereum: {
    id: "ethereum",
    symbol: "ETH",
    networkName: (
      <Row type="sc" sx={{ gap: "12px" }}>
        <img src={EthereumIcon} alt="ETH" width="25px" height="24px" />
        <Typography variant="body-md">Ethereum</Typography>
      </Row>
    ),
    walletName: "ethAddress",
  },
  base: {
    id: "base",
    symbol: "ETH(BASE)",
    networkName: (
      <Row type="sc" sx={{ gap: "12px" }}>
        <img src={BaseIcon} alt="BASE" width="25px" height="24px" />
        <Typography variant="body-md">Base</Typography>
      </Row>
    ),
    walletName: "ethAddress",
  },
  berachain: {
    id: "berachain",
    symbol: "BERA",
    networkName: (
      <Row type="sc" sx={{ gap: "12px" }}>
        <img src={BeraIcon} alt="BERA" width="25px" height="24px" />
        <Typography variant="body-md">Berachain</Typography>
      </Row>
    ),
    walletName: "ethAddress",
  },
  bsc: {
    id: "bsc",
    symbol: "BNB",
    networkName: (
      <Row type="sc" sx={{ gap: "12px" }}>
        <img src={BinanceIcon} alt="BNB" width="25px" height="24px" />
        <Typography variant="body-md">Binance Smart Chain (BSC)</Typography>
      </Row>
    ),
    walletName: "ethAddress",
  },
};

const withCrossChainDeposit = (Component: React.ComponentType<any>) => {
  return ({
    defaultChain = "ethereum",
    chains,
    isOpen,
    close,
    onCopySuccess,
    ...props
  }: {
    defaultChain: Chain;
    chains: Array<Chain>;
    isOpen: boolean;
    close: () => void;
    onCopySuccess?: () => void;
  }) => {
    const [selectedChain, setSelectedChain] = useState<Chain>(defaultChain);
    const { walletAddresses } = useUserStoreV2();

    const sortedConfigs = useMemo(() => {
      const config = chains
        // Map to cross chain config
        .map((chains) => {
          return crossChainConfigs[chains];
        })
        // Sort by default token type
        .sort((a, b) => {
          const isAMatch = a.id === defaultChain ? 1 : -1;
          const isBMatch = b.id === defaultChain ? 1 : -1;

          return isAMatch - isBMatch;
        });
      return config;
    }, [chains, defaultChain]);

    const supporttedChainList = useMemo(() => {
      const result = sortedConfigs.map((config) => {
        return config.symbol;
      });
      return result;
    }, [sortedConfigs]);

    const handleCopy = () => {
      if (!walletAddresses) return;
      const walletAddress = walletAddresses[crossChainConfigs[selectedChain].walletName];
      if (walletAddress !== undefined) {
        const success = copyToClipboard(walletAddress);
        if (success) {
          onCopySuccess?.();
        }
      }
    };
    return (
      <Component
        {...props}
        defaultChain={defaultChain}
        chains={chains}
        isOpen={isOpen}
        close={close}
        sortedConfigs={sortedConfigs}
        onCopySuccess={onCopySuccess}
        supporttedChainList={supporttedChainList}
        handleCopy={handleCopy}
        selectedChain={selectedChain}
        setSelectedChain={setSelectedChain}
        walletAddresses={walletAddresses}
      />
    );
  };
};

export { withCrossChainDeposit };
